import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Circle, Mail, Send } from 'lucide-react';
import { useNudgePreview, useSendActivationNudge } from '@/hooks/useAdminUsers';

export const NUDGE_STEP_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'patient_created', label: 'Cadastrar o paciente' },
  { key: 'care_plan_created', label: 'Criar o plano de cuidado' },
  { key: 'shift_template_created', label: 'Configurar a escala de plantão' },
  { key: 'team_invited', label: 'Convidar a equipe de cuidado' },
  { key: 'team_joined', label: 'Convite pendente de aceite' },
  { key: 'first_shift', label: 'Iniciar o primeiro plantão' },
  { key: 'first_task_executed', label: 'Registrar a primeira tarefa' },
  { key: 'subscribed', label: 'Escolher um plano' },
  { key: 'signup', label: 'Confirmar o cadastro' },
];

const ERROR_MESSAGES: Record<string, string> = {
  email_not_confirmed: 'O usuário ainda não confirmou o e-mail — o envio foi bloqueado.',
  email_suppressed: 'Este e-mail está na lista de supressão (bounce, reclamação ou descadastro).',
  recently_contacted: 'Já enviamos um e-mail de estímulo para este usuário nas últimas 72h.',
  invalid_funnel_step: 'Passo do funil inválido.',
  send_failed: 'Falha ao enviar o e-mail. Tente novamente em alguns minutos.',
};

function parseError(message: string): { key: string; text: string } {
  let key = '';
  try {
    key = JSON.parse(message)?.error ?? '';
  } catch {
    const m = message.match(/"error"\s*:\s*"([a-z_]+)"/);
    key = m?.[1] ?? '';
  }
  return { key, text: ERROR_MESSAGES[key] ?? 'Não foi possível enviar o e-mail.' };
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  defaultStep: string | null;
  daysStuck?: number | null;
}

export function ActivationNudgeDialog({
  open, onOpenChange, userId, userName, userEmail, defaultStep, daysStuck,
}: Props) {
  const [step, setStep] = useState(defaultStep ?? 'patient_created');
  const [message, setMessage] = useState('');
  const [force, setForce] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(defaultStep && NUDGE_STEP_OPTIONS.some(o => o.key === defaultStep) ? defaultStep : 'patient_created');
      setMessage('');
      setForce(false);
    }
  }, [open, defaultStep]);

  const preview = useNudgePreview({
    userId, funnelStep: step, customMessage: message.trim() || null, daysStuck, enabled: open,
  });
  const send = useSendActivationNudge();

  const handleSend = () => {
    send.mutate(
      { userId, funnelStep: step, customMessage: message.trim() || null, daysStuck, force },
      {
        onSuccess: () => {
          toast.success('E-mail de estímulo enviado', { description: userEmail ?? undefined });
          onOpenChange(false);
        },
        onError: (e: any) => {
          const { key, text } = parseError(String(e?.message ?? ''));
          if (key === 'recently_contacted') {
            setForce(true);
            toast.warning(text, { description: 'Clique novamente em enviar para forçar o envio.' });
          } else {
            toast.error(text);
          }
        },
      },
    );
  };

  const d = preview.data?.template_data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            Enviar e-mail de estímulo
          </DialogTitle>
          <DialogDescription>
            {userName || 'Usuário'} · {userEmail ?? 'sem e-mail'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Passo em que o usuário parou</Label>
            <Select value={step} onValueChange={setStep}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {NUDGE_STEP_OPTIONS.map(o => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                    {o.key === defaultStep && ' (detectado)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Mensagem pessoal (opcional)</Label>
            <Textarea
              value={message}
              maxLength={600}
              rows={3}
              placeholder="Ex.: Qualquer dúvida me chama, posso montar o primeiro plano junto com você."
              onChange={(e) => setMessage(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{message.length}/600</p>
          </div>

          <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Pré-visualização
            </p>
            {preview.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : d ? (
              <div className="space-y-3">
                <p className="font-semibold text-sm">{d.stepHeadline}</p>
                <p className="text-sm text-muted-foreground">
                  Olá {(d.recipientName ?? '').trim().split(/\s+/)[0] || '—'},
                </p>
                <div className="rounded-md border-l-4 border-primary bg-background p-2">
                  <p className="text-[11px] uppercase text-muted-foreground">Você parou aqui</p>
                  <p className="text-sm font-medium">{d.stepLabel}</p>
                  {d.daysStuck ? (
                    <p className="text-xs text-muted-foreground">há {d.daysStuck} dia(s)</p>
                  ) : null}
                </div>
                <p className="text-sm">{d.stepDescription}</p>
                <div className="space-y-1">
                  {d.steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {s.state === 'done'
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                        : <Circle className={`h-3.5 w-3.5 shrink-0 ${s.state === 'current' ? 'text-primary' : 'text-muted-foreground'}`} />}
                      <span className={s.state === 'current' ? 'font-medium' : s.state === 'done' ? 'text-success' : 'text-muted-foreground'}>
                        {s.label}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-primary">{d.benefit}</p>
                {d.adminMessage && (
                  <p className="text-sm border border-dashed border-border rounded-md p-2 whitespace-pre-wrap">
                    {d.adminMessage}
                  </p>
                )}
                <Badge className="bg-primary text-primary-foreground">{d.ctaLabel}</Badge>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Não foi possível gerar a pré-visualização.</p>
            )}
          </div>

          {force && (
            <p className="text-xs text-warning flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              Envio forçado: já houve contato nas últimas 72h.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSend} disabled={send.isPending}>
            <Send className="h-4 w-4 mr-1" />
            {send.isPending ? 'Enviando...' : force ? 'Enviar mesmo assim' : 'Enviar e-mail'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
